import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProducts } from './api/api';
import { IProduct } from './api/types';
import { useAuth } from 'react-oidc-context';
import { isDevMode } from './Components/Auth/auth';
import { clearDevToken } from './Components/Auth/DevAuth';
import { clearLastVisitedUrl } from './Components/Hooks/urlPersistence';

// Module-level cache to prevent duplicate fetches across component remounts
let cachedProducts: IProduct[] | null = null;
let fetchPromise: Promise<void> | null = null;
let lastFailure: {
  status: 'ERROR' | 'FORBIDDEN';
  errors?: string[];
} | null = null;

// Logging out and back in may switch to a different account (e.g. one that
// has since been whitelisted or granted a role), so a fresh session must
// not be stuck showing a stale cached result - including a stale failure.
export const resetStaticDataCache = () => {
  cachedProducts = null;
  fetchPromise = null;
  lastFailure = null;
};

export interface ITenant {
  id: number;
  name: string;
}

export interface StaticData {
  products: Array<IProduct>;
}

type ContextState =
  | { status: 'LOADING' }
  | { status: 'ERROR'; errors?: string[] }
  | { status: 'FORBIDDEN'; errors?: string[] }
  | { status: 'LOADED'; staticData: StaticData };

// Default empty context value (stable reference)
const EMPTY_STATIC_DATA: StaticData = { products: [] };
const Context = React.createContext<StaticData>(EMPTY_STATIC_DATA);

export const useStaticData = (): StaticData => {
  const contextState = React.useContext(Context);
  if (contextState === null) {
    throw new Error(
      'useStaticData must be used within a useStaticData tag',
    );
  }
  return contextState;
};

export const StaticDataProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  // isDevMode is a build-time constant, so this never varies across renders
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = isDevMode ? null : useAuth();
  const navigate = useNavigate();
  const [state, setState] = React.useState<ContextState>(() => {
    // Initialize with cached data if available
    if (cachedProducts) {
      return {
        status: 'LOADED',
        staticData: { products: cachedProducts },
      };
    }
    return { status: 'LOADING' };
  });

  useEffect(() => {
    let isMounted = true;

    const applySettledState = () => {
      if (!isMounted) return false;
      if (cachedProducts) {
        setState({
          status: 'LOADED',
          staticData: { products: cachedProducts },
        });
        return true;
      }
      if (lastFailure) {
        setState(lastFailure);
        return true;
      }
      return false;
    };

    // If we already have a settled result, ensure state is updated.
    // Handles the case where this provider remounts (e.g. a logout then
    // login) while a fetch started by a prior mount is still in flight
    // and resolves in the gap between this mount's render and this
    // effect running.

    if (applySettledState()) {
      return;
    }

    // If a fetch is already in progress, poll for completion
    if (fetchPromise) {
      const pollInterval = setInterval(() => {
        if (applySettledState()) {
          clearInterval(pollInterval);
        }
      }, 100); // Check every 100ms

      return () => {
        isMounted = false;
        clearInterval(pollInterval);
      };
    }

    // Start a new fetch
    fetchPromise = (async (): Promise<void> => {
      try {
        const [products] = await Promise.all([getProducts()]);
        if (products.status === 'Error') {
          console.error(products.errorMessages);
          lastFailure = {
            status: 'ERROR',
            errors: products.errorMessages,
          };
          fetchPromise = null; // Allow retry on error
        }
        if (products.status === 'Forbidden') {
          console.error(products.errorMessages);
          lastFailure = {
            status: 'FORBIDDEN',
            errors: products.errorMessages,
          };
          // Deliberately don't reset fetchPromise here - Forbidden means
          // this user isn't whitelisted, which won't change by retrying.
          // Leaving fetchPromise set means later mounts settle from
          // lastFailure instead of re-hitting the API.
        }
        if (products.status === 'Not Found') {
          console.warn(
            'Products not found: ',
            products.errorMessages,
          );
          lastFailure = {
            status: 'ERROR',
            errors: products.errorMessages,
          };
          fetchPromise = null; //Allow retry on 404
        }
        if (products.status === 'Network issue') {
          console.warn(
            'Network issue reported: ',
            products.errorMessages,
          );
          lastFailure = {
            status: 'ERROR',
            errors: products.errorMessages,
          };
          fetchPromise = null;
        }
        if (products.status === 'OK') {
          cachedProducts = products.data; // Cache the results
          lastFailure = null;
          fetchPromise = null;
        }
      } catch (error) {
        console.error(error);
        lastFailure = { status: 'ERROR' };
        fetchPromise = null; // Allow retry on error
      }
    })();

    // Set up polling to wait for the fetch to settle (even for the
    // component that started the fetch)
    const pollInterval = setInterval(() => {
      if (applySettledState()) {
        clearInterval(pollInterval);
      }
    }, 100); // Check every 100ms

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
    };
  }, []);

  const contextValue = React.useMemo(() => {
    return state.status === 'LOADED'
      ? state.staticData
      : EMPTY_STATIC_DATA;
  }, [state]);

  if (state.status === 'LOADED') {
    return (
      <Context.Provider value={contextValue}>
        {children}
      </Context.Provider>
    );
  }

  if (state.status === 'LOADING') {
    return <div>Loading data</div>;
  }

  const handleLogout = () => {
    clearLastVisitedUrl();
    resetStaticDataCache();
    if (isDevMode) {
      clearDevToken();
      navigate('/login');
    } else {
      auth?.removeUser();
    }
  };

  const isForbidden = state.status === 'FORBIDDEN';

  return (
    <div className="flex h-screen justify-center items-center">
      <div className="flex flex-col items-center bg-gray-50 max-w-sm rounded px-6 py-4 ">
        <div className="font-bold text-l p-2">
          {isForbidden ? 'Forbidden' : 'Something went wrong 😭'}
        </div>
        <div className="text-s p-2">
          {isForbidden ? (
            <>
              You don't have permission to view this. Either your
              session doesn't have Global Admin PIM enabled, or your
              account hasn't been whitelisted yet - ask{' '}
              <a
                href="https://easeenorway.slack.com/archives/C02D8V3FM25"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                #booster-platform
              </a>{' '}
              to check the latter.
            </>
          ) : (
            'Make sure you have logged with an Easee employee account that has admin access.'
          )}
        </div>
        <button
          onClick={handleLogout}
          className={`bg-blue-500 text-white text-s p-3 rounded hover:bg-blue-700`}
          type="button"
        >
          Log out
        </button>
      </div>
    </div>
  );
};
